import { Price } from '@wix/package-picker-components';
import { observer } from 'mobx-react';
import { AdvancedPlansProductModel } from 'models/AdvancedPlansProductModel';
import { RootStore, withInjectedRootStore } from 'providers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { taxIncludedTranslationKey } from '../../../../../../constants';
import { Experiments, PlanType, ProductChangeType } from 'enums';

interface ProductCardProps {
  product: AdvancedPlansProductModel;
  size: 'small' | 'tiny';
  rootStore: RootStore;
}

const ProductPriceComp: React.FC<ProductCardProps> = ({
  product,
  size,
  rootStore,
}) => {
  const { t } = useTranslation();
  const {
    appContext: { country, currentPlanDetails, isLimitedTimeDiscount },
    pricesIncludeTax,
    currentCycle,
    isChangePlan,
    isInSale,
    experiments,
    planType,
  } = rootStore.appStore;
  const cycle = currentPlanDetails?.paymentCycle || currentCycle;
  const unifiedChangePlanPhase1Enabled =
    experiments[Experiments.UnifiedChangePlanPhase1] === 'true';
  const shouldShowStudioSaleInChangePlan =
    experiments[Experiments.changePlanStudioSale] === 'true';
  const shouldShowPriceByCycle =
    isChangePlan && !unifiedChangePlanPhase1Enabled;

  const fullPrice = shouldShowPriceByCycle
    ? product.getFullPriceByCycle(cycle)
    : product.getYearlyFullPrice();

  const actualPriceBeforeUnified = isChangePlan
    ? product.getActualPriceByCycle(cycle)
    : product.getFirstCycleYearlyMonthly() || product.getYearlyActualPrice();

  const getActualPrice = () => {
    if (shouldShowPriceByCycle) {
      return product.getActualPriceByCycle(cycle);
    }

    if (
      isChangePlan &&
      product.getProductChangeType() !== ProductChangeType.Upgrade
    ) {
      return fullPrice;
    }

    return (
      product.getFirstCycleYearlyMonthly() || product.getYearlyActualPrice()
    );
  };
  const isDayfulFreeProduct = product.getName() === 'Free';

  const _fullPrice = isDayfulFreeProduct ? 0 : fullPrice;
  const actualPriceWithUnified = isDayfulFreeProduct ? 0 : getActualPrice();

  const shouldShowSale =
    isChangePlan &&
      unifiedChangePlanPhase1Enabled &&
      product.getProductChangeType() !== ProductChangeType.Upgrade
      ? false
      : isInSale;

  const shouldShowProductFullPrice =
    shouldShowSale || (isLimitedTimeDiscount && planType === PlanType.Business);


  return (
    <Price
      size={size}
      price={shouldShowStudioSaleInChangePlan ? actualPriceWithUnified : actualPriceBeforeUnified}
      fullPrice={_fullPrice}
      cycleLabel={`/${t('product_price.month')}`}
      taxIncludedLabel={t(taxIncludedTranslationKey[country])}
      shouldShowTaxIncludedLabel={pricesIncludeTax}
      shouldShowProductFullPrice={shouldShowProductFullPrice}
    />
  );
};

export const ProductPrice = observer(withInjectedRootStore(ProductPriceComp));
